import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken =
  "pk.eyJ1IjoibWotZXBzaWxvbiIsImEiOiJja24wcWV5eTcwcTY5MnZwazA0cTRxMTlwIn0.powZMmJIS2FoR4JY1DFSGg";

const MAPBOX_STYLE = "mapbox://styles/mapbox/light-v11";

/**
 * Props:
 *   officers  — [{ name, lng, lat }]
 *   customers — [{ name, lng, lat }]
 *   loading   — boolean
 */
export default function DashboardMap({ officers = [], customers = [], loading = false }) {
  const mapContainer = useRef(null);
  const map          = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Init map once
  useEffect(() => {
    if (!mapContainer.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style:     MAPBOX_STYLE,
      center:    [-98.5795, 39.8283], // US geographic center
      zoom:      4,
    });

    map.current.addControl(new mapboxgl.FullscreenControl(), "top-right");
    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    map.current.on("load", () => setMapLoaded(true));

    return () => { if (map.current) map.current.remove(); };
  }, []);

  // Officer markers (blue)
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    document.querySelectorAll(".officer-pin").forEach((el) => el.remove());

    officers.forEach((officer) => {
      if (!officer.lat || !officer.lng) return;

      const el = document.createElement("div");
      el.className = "officer-pin";
      Object.assign(el.style, {
        width: "9px", height: "9px",
        background: "#404880",
        borderRadius: "50%",
        border: "2px solid white",
        boxShadow: "0 1px 4px rgba(64,72,128,0.4)",
        cursor: "pointer",
      });

      new mapboxgl.Marker(el)
        .setLngLat([officer.lng, officer.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 10, closeButton: false }).setHTML(
            `<div style="padding:4px 8px;font-size:11px;font-weight:500;">${officer.name}</div>`
          )
        )
        .addTo(map.current);
    });
  }, [officers, mapLoaded]);

  // Customer markers (brand-pink)
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    document.querySelectorAll(".customer-pin").forEach((el) => el.remove());

    customers.forEach((customer) => {
      if (!customer.lat || !customer.lng) return;

      const el = document.createElement("div");
      el.className = "customer-pin";
      Object.assign(el.style, {
        width: "9px", height: "9px",
        background: "#CC457D",
        borderRadius: "50%",
        border: "2px solid white",
        boxShadow: "0 1px 4px rgba(204,69,125,0.4)",
        cursor: "pointer",
      });

      new mapboxgl.Marker(el)
        .setLngLat([customer.lng, customer.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 10, closeButton: false }).setHTML(
            `<div style="padding:4px 8px;font-size:11px;font-weight:500;">${customer.name}</div>`
          )
        )
        .addTo(map.current);
    });

    // Fit bounds when there are markers
    const allMarkers = [...officers, ...customers].filter((m) => m.lat && m.lng);
    if (allMarkers.length > 0 && map.current) {
      const bounds = new mapboxgl.LngLatBounds();
      allMarkers.forEach((m) => bounds.extend([m.lng, m.lat]));
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 12, duration: 600 });
    }
  }, [customers, mapLoaded]);

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden">
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />

      {/* Legend */}
      <div className="absolute bottom-3 right-3 z-10 flex flex-col gap-1.5 rounded-md bg-white px-3 py-2 shadow-md text-xs">
        <div className="flex items-center gap-2">
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#404880", border: "1.5px solid white", boxShadow: "0 1px 3px rgba(64,72,128,0.3)" }} />
          <span className="font-medium text-gray-700">Officers ({officers.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#CC457D", border: "1.5px solid white", boxShadow: "0 1px 3px rgba(204,69,125,0.3)" }} />
          <span className="font-medium text-gray-700">Customers ({customers.length})</span>
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/30">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-pink border-t-transparent" />
        </div>
      )}
    </div>
  );
}
